/**
 * The network, shown at the global stop: arcs flown through the air between the
 * undersea cables' landings, one arc for each stretch of cable between two
 * landings (or junctions, or the places on the page), with a pulse running
 * along each. Data and routing are in `data/cables`.
 */
import { buildCableNetwork, DESTINATIONS } from './data/cables'
import { angleBetween, type Vec3 } from './data/geo'
import { glow } from './draw'
import { smoothstep } from './easing'
import { projector, type Projected } from './projection'
import type { Canvas2D, View } from './renderer/renderer'

/* =====================================================================
   TUNING
   ===================================================================== */
const NET_LIFT = 0.13 /* how high the arcs rise off the surface (× the globe's radius) */
const NET_SPEED = 0.22 /* how fast the pulses travel (lengths per second) */
const MIN_ARC = 0.05 /* rad: a stretch shorter than this is too short to read as an arc */
const ARC_STEPS = 36 /* segments along an arc */
const PULSE_STEPS = 10 /* segments in a pulse's tail */
/* Seeds the arcs' start delays, pulse phases and speeds. It is the state the
   random sequence had reached in the original design when it came to these arcs (the
   styles that shared it are gone), so the arcs still match the design. */
const ARC_SEED = 1378633128
/* ===================================================================== */

export const STROKE = '242,215,174'
const PULSE = '255,236,205'

interface Arc {
  readonly A: Vec3
  readonly B: Vec3
  readonly ang: number
  readonly sa: number
  readonly delay: number
  readonly ph: number
  readonly spd: number
}

function buildArcs(): Arc[] {
  const cables = buildCableNetwork()
  const nodes = cables.nodes
  let seed = ARC_SEED
  function random(): number {
    seed = (seed * 16807) % 2147483647
    return seed / 2147483647
  }

  const keep = new Set<number>([cables.start])
  for (const d of DESTINATIONS) keep.add(cables.node(d))
  const seen = new Set<string>()
  const arcs: Arc[] = []
  for (const route of cables.routes) {
    const last = route.waypoints.length - 1
    const landings = route.waypoints.filter(
      (w, k) => k === 0 || k === last || nodes[w.node].degree >= 2 || keep.has(w.node)
    )
    for (let k = 0; k + 1 < landings.length; k++) {
      const a = landings[k].node
      const b = landings[k + 1].node
      const key = Math.min(a, b) + '-' + Math.max(a, b)
      if (a === b || seen.has(key)) continue
      const ang = angleBetween(nodes[a].point, nodes[b].point)
      if (ang < MIN_ARC) continue
      seen.add(key)
      arcs.push({
        A: nodes[a].point,
        B: nodes[b].point,
        ang,
        sa: Math.sin(ang),
        delay: random() * 0.6,
        ph: random(),
        spd: (NET_SPEED * (0.7 + 0.6 * random())) / Math.max(ang, 0.4),
      })
    }
  }
  return arcs
}

/** The arcs of the network. */
export const NETWORK_ARCS: readonly Arc[] = buildArcs()

/**
 * Create the network. Draws the undersea-cable arcs and their pulses
 * onto `ctx` at the global stop.
 */
export function createNetwork(ctx: Canvas2D) {
  const arcs = NETWORK_ARCS
  /* the projector for the current frame; reset by draw() */
  let proj = projector({ cx: 0, cy: 0, Rs: 0, d: 2, lam: 0, tilt: 0 })

  return {
    /** Draw the network through camera `v` at time `t`, `w` being how present the global stop is (0..1). */
    draw(v: View, t: number, w: number): void {
      if (w < 0.003) return
      proj = projector(v)
      ctx.globalCompositeOperation = 'lighter'
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      drawArcs(t, w)
    },
  }

  function drawArcs(t: number, w: number): void {
    for (let i = 0; i < arcs.length; i++) {
      const a = arcs[i]
      const ap = smoothstep(a.delay, a.delay + 0.3, w)
      if (ap < 0.003) continue
      const blink =
        ap < 1 ? 0.55 + 0.45 * Math.sin(t * 38 + i * 2.1) : 1 /* it flickers as it comes on */
      ctx.strokeStyle = 'rgba(' + STROKE + ',' + (0.17 * ap * blink * w).toFixed(3) + ')'
      ctx.lineWidth = 1
      ctx.beginPath()
      let open = false
      let p: Projected
      for (let j = 0; j < ARC_STEPS; j++) {
        p = arcAt(a, j / (ARC_STEPS - 1))
        if (!p.vis) {
          open = false
          continue
        }
        if (open) ctx.lineTo(p.x, p.y)
        else {
          ctx.moveTo(p.x, p.y)
          open = true
        }
      }
      ctx.stroke()
      for (let e = 0; e < 2; e++) {
        p = arcAt(a, e)
        if (p.vis) {
          ctx.fillStyle = 'rgba(' + STROKE + ',' + (0.65 * ap * w).toFixed(3) + ')'
          ctx.beginPath()
          ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2)
          ctx.fill()
        }
      }
      /* the pulse: placed at its exact position along the arc every frame, with
         a tail sampled behind it, so it glides instead of stepping */
      const hp = (t * a.spd + a.ph) % 1
      const tail = Math.min(0.16, 0.5 / (a.ang * 10)) + 0.05
      const pa = 0.8 * ap * w * Math.sin(Math.PI * hp)
      if (pa < 0.01) continue
      const xs: number[] = []
      const ys: number[] = []
      let ok = true
      for (let q = 0; q <= PULSE_STEPS; q++) {
        const uq = Math.max(0, hp - tail * (1 - q / PULSE_STEPS))
        p = arcAt(a, uq)
        if (!p.vis) {
          ok = false
          break
        }
        xs.push(p.x)
        ys.push(p.y)
      }
      if (!ok) continue
      const g = ctx.createLinearGradient(xs[0], ys[0], xs[PULSE_STEPS], ys[PULSE_STEPS])
      g.addColorStop(0, 'rgba(' + PULSE + ',0)')
      g.addColorStop(1, 'rgba(' + PULSE + ',' + pa.toFixed(3) + ')')
      ctx.strokeStyle = g
      ctx.lineWidth = 1.6
      ctx.beginPath()
      ctx.moveTo(xs[0], ys[0])
      for (let q = 1; q <= PULSE_STEPS; q++) ctx.lineTo(xs[q], ys[q])
      ctx.stroke()
      glow(ctx, xs[PULSE_STEPS], ys[PULSE_STEPS], 7, pa, 0.25, PULSE)
    }
  }

  /** A point on the arc, lifted off the surface. */
  function arcAt(a: Arc, u: number): Projected {
    const fa = Math.sin((1 - u) * a.ang) / a.sa
    const fb = Math.sin(u * a.ang) / a.sa
    const lift = 1 + NET_LIFT * Math.sin(Math.PI * u) * Math.min(a.ang / 1.2, 1)
    return proj.project(
      (a.A[0] * fa + a.B[0] * fb) * lift,
      (a.A[1] * fa + a.B[1] * fb) * lift,
      (a.A[2] * fa + a.B[2] * fb) * lift
    )
  }
}

export type Network = ReturnType<typeof createNetwork>
